<i18n src="./translations"></i18n>

<template>
  <div
    class="pdp-vehicle-notification"
    :class="[
      `pdp-vehicle-notification--${compatibilityClasses[displayCompatibility]}`,
      {
        'pdp-vehicle-notification--expandable': hasLimitations,
        'pdp-vehicle-notification--expanded':
          hasLimitations && isContentVisible,
      },
    ]"
    :data-preload="[$options.name]"
  >
    <button
      v-if="hasLimitations && isCollapsible"
      type="button"
      :class="['pdp-vehicle-notification__toggle', gtmClassName + '-toggle']"
      :aria-expanded="isExpanded ? 'true' : 'false'"
      @click="toggleExpanded"
    >
      <component
        :is="compatibilityIcons[vehicleCompatibility]"
        class="pdp-vehicle-notification__icon"
        aria-hidden="true"
      />

      <span class="pdp-vehicle-notification__headline">
        {{ $t('pdp-vehicle-notification.item-fits-for-with-limitations') }}
      </span>

      <ChevronDownSvg
        :class="[
          'pdp-vehicle-notification__chevron',
          { 'pdp-vehicle-notification__chevron--expanded': isExpanded },
        ]"
        aria-hidden="true"
      />
    </button>

    <div
      v-else-if="hasLimitations && !isCollapsible"
      :class="[
        'pdp-vehicle-notification__toggle',
        'pdp-vehicle-notification__toggle--static',
      ]"
    >
      <component
        :is="compatibilityIcons[vehicleCompatibility]"
        class="pdp-vehicle-notification__icon"
        aria-hidden="true"
      />

      <p class="pdp-vehicle-notification__headline">
        {{ $t('pdp-vehicle-notification.item-fits-for-with-limitations') }}
      </p>
    </div>

    <template v-else>
      <component
        :is="compatibilityIcons[displayCompatibility]"
        class="pdp-vehicle-notification__icon"
        aria-hidden="true"
      />

      <div class="pdp-vehicle-notification__content">
        <p class="pdp-vehicle-notification__headline">
          {{ $t(compatibilityTranslations[displayCompatibility]) }}
        </p>

        <p
          v-if="displayCompatibility === 'SELECTED_CAR_OK'"
          class="pdp-vehicle-notification__description"
        >
          {{ $t('pdp-vehicle-notification.check-attributes-hint') }}
        </p>

        <button
          v-if="vehicleCompatibility === 'NOT_SELECTED'"
          :class="['pdp-vehicle-notification__link', gtmClassName + '-link']"
          type="button"
          :aria-label="$t('pdp-vehicle-notification.select-a-car')"
          @click="openWidget"
        >
          {{ $t('pdp-vehicle-notification.select-a-car') }}
        </button>

        <!--  TODO: [ozlui] find the destination for this link  -->
        <MaskedLink
          v-if="
            vehicleCompatibility === 'SELECTED_CAR_NOT_OK' &&
            relatedItemsLink !== '/' &&
            relatedItemsLink !== ''
          "
          class="pdp-vehicle-notification__link"
          :redirect-to="relatedItemsLink"
          :text="$t('pdp-vehicle-notification.to-fitting-items')"
        />
      </div>
    </template>

    <div
      v-if="hasLimitations && isContentVisible && attributesVariant !== 'chips'"
      class="pdp-vehicle-notification__attributes pdp-vehicle-notification__attributes--list"
    >
      <div
        v-for="attribute in visibleListAttributes"
        :key="itemKey(attribute)"
        class="pdp-vehicle-notification__row"
      >
        <span class="pdp-vehicle-notification__label"
          >{{ attribute.label }}:</span
        >
        <span class="pdp-vehicle-notification__value">{{
          attribute.info
        }}</span>
      </div>

      <button
        v-if="hasMoreListAttributes && !moreAttributesLink"
        type="button"
        class="pdp-vehicle-notification__more"
        @click="toggleListExpanded"
      >
        {{
          isListExpanded
            ? $t('pdp-vehicle-notification.less-limitations')
            : moreLimitationsLabel
        }}
      </button>

      <MaskedLink
        v-if="hasMoreListAttributes && moreAttributesLink"
        class="pdp-vehicle-notification__more"
        :redirect-to="moreAttributesLink"
        :text="moreLimitationsLabel"
      />
    </div>

    <div
      v-else-if="hasLimitations && attributesVariant === 'chips'"
      class="pdp-vehicle-notification__attributes pdp-vehicle-notification__attributes--chips"
    >
      <span
        v-for="attribute in visibleChipAttributes"
        :key="attribute.id || `${attribute.label}-${attribute.info}`"
        class="pdp-vehicle-notification__chip"
        role="link"
        tabindex="0"
        @click="handleChipClick"
        @keydown.enter.prevent="handleChipClick"
        @keydown.space.prevent="handleChipClick"
      >
        {{ attribute.label }}: {{ attribute.info }}
      </span>

      <span
        v-if="hasMoreChipAttributes"
        class="pdp-vehicle-notification__chip pdp-vehicle-notification__chip--overflow"
        role="link"
        tabindex="0"
        @click="handleChipClick"
        @keydown.enter.prevent="handleChipClick"
        @keydown.space.prevent="handleChipClick"
      >
        …
      </span>
    </div>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import { mapMutations } from 'vuex';
import MaskedLink from 'Components/01-atoms/masked-link/MaskedLink';
import CarCheckFitSvg from 'Components/00-generated/CarCheckFitSvg';
import CarCheckNotFitSvg from 'Components/00-generated/CarCheckNotFitSvg';
import CarCheckUnknownSvg from 'Components/00-generated/CarCheckUnknownSvg';
import ChevronDownSvg from 'Components/00-generated/ChevronDownSvg';

export default {
  name: 'PdpVehicleNotification',
  components: {
    MaskedLink,
    CarCheckFitSvg,
    CarCheckNotFitSvg,
    CarCheckUnknownSvg,
    ChevronDownSvg,
  },
  mixins: [globalMixin],
  props: {
    vehicleCompatibility: {
      default: 'NOT_SELECTED',
      type: String,
    },
    vehicleName: {
      default: '',
      type: String,
    },
    isCarSelected: {
      type: Boolean,
      default: false,
    },
    relatedItemsLink: {
      type: String,
      default: '',
    },
    carAttributes: {
      type: Array,
      default: () => [],
    },
    carAttributesLimit: {
      type: Number,
      default: 6,
    },
    moreAttributesLink: {
      type: String,
      default: '',
    },
    attributesVariant: {
      type: String,
      default: 'list',
      validator: (value) => ['list', 'list-expanded', 'chips'].includes(value),
    },
    carAttributesChipsLimit: {
      type: Number,
      default: 2,
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: this.defaultExpanded,
      isListExpanded: false,
      compatibilityTranslations: {
        SELECTED_CAR_OK: 'pdp-vehicle-notification.item-fits-for',
        SELECTED_CAR_NOT_OK: 'pdp-vehicle-notification.item-does-not-fit-for',
        NOT_SELECTED: 'pdp-vehicle-notification.check-if-vehicle-fits',
        UNIVERSAL_PRODUCT: 'pdp-vehicle-notification.can-be-used-universally',
      },
      compatibilityClasses: {
        SELECTED_CAR_OK: 'car-ok qa-selectedCarOk',
        SELECTED_CAR_NOT_OK: 'car-not-ok qa-selectedCarNotOk',
        NOT_SELECTED: 'car-not-selected qa-carNotSelected',
        UNIVERSAL_PRODUCT: 'universal-product qa-universalProduct',
      },
      compatibilityIcons: {
        SELECTED_CAR_OK: 'CarCheckFitSvg',
        SELECTED_CAR_NOT_OK: 'CarCheckNotFitSvg',
        NOT_SELECTED: 'CarCheckUnknownSvg',
        UNIVERSAL_PRODUCT: 'CarCheckFitSvg',
      },
    };
  },
  computed: {
    displayCompatibility() {
      if (
        this.vehicleCompatibility === 'UNIVERSAL_PRODUCT' &&
        this.isCarSelected
      ) {
        return 'SELECTED_CAR_OK';
      }

      return this.vehicleCompatibility;
    },
    hasLimitations() {
      return (
        this.vehicleCompatibility === 'SELECTED_CAR_OK' &&
        this.carAttributes.length > 0
      );
    },
    isCollapsible() {
      return this.attributesVariant === 'list';
    },
    isContentVisible() {
      return !this.isCollapsible || this.isExpanded;
    },
    visibleChipAttributes() {
      return this.carAttributes.slice(0, this.carAttributesChipsLimit);
    },
    hasMoreChipAttributes() {
      return this.carAttributes.length > this.carAttributesChipsLimit;
    },
    visibleListAttributes() {
      if (this.attributesVariant === 'list-expanded') {
        return this.carAttributes;
      }

      return this.isListExpanded
        ? this.carAttributes
        : this.carAttributes.slice(0, this.carAttributesLimit);
    },
    hasMoreListAttributes() {
      if (this.attributesVariant === 'list-expanded') {
        return false;
      }

      return this.carAttributes.length > this.carAttributesLimit;
    },
    moreLimitationsLabel() {
      const count = this.carAttributes.length - this.carAttributesLimit;
      if (count <= 0) return '';

      return this.$t('pdp-vehicle-notification.additional-limitations', {
        '%count%': count,
      });
    },
  },
  watch: {
    defaultExpanded(newVal) {
      this.isExpanded = newVal;
    },
  },
  methods: {
    ...mapMutations('carSelection', {
      openWidget: 'CARIS_DIALOG__OPEN',
    }),
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    toggleListExpanded() {
      this.isListExpanded = !this.isListExpanded;
    },
    itemKey(attribute) {
      return attribute.id || `${attribute.label}-${attribute.info}`;
    },
    handleChipClick() {
      this.$emit('scroll-to-attributes');
    },
  },
};
</script>

<style scoped lang="scss">
@import 'mixins';
@import 'variables/breakpoints';

.pdp-vehicle-notification {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-1--half);
  min-width: 100%;
  min-height: 48px;
  border-radius: 8px;
  border: 1px solid transparent;
  padding: var(--space-1--half);
  letter-spacing: -0.1px;
  color: var(--color-nero);

  @media #{$_mediaMDown} {
    border-radius: 8px;
  }

  background-color: var(--color-cci-blue-light);
  border-color: var(--color-kfzteile24-primary);

  &--car-ok,
  &--universal-product {
    background-color: var(--color-success-green);
    border-color: var(--color-kfzteile24-semantic-green);
  }

  &--car-not-ok {
    background-color: var(--color-cci-red-lighter);
    border-color: var(--color-cci-red-light);
  }

  &--car-not-selected {
    background-color: var(--color-cci-blue-light);
    border-color: var(--color-kfzteile24-primary);
  }

  &--expandable {
    flex-direction: column;
    align-items: stretch;
    padding: 0;
    overflow: hidden;
    background-color: transparent;
    border-color: transparent;
  }

  &--expandable.pdp-vehicle-notification--expanded {
    border-color: var(--color-kfzteile24-semantic-green);
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: var(--space-1--half);
    width: 100%;
    margin: 0;
    padding: var(--space-1--half);
    border: 1px solid var(--color-kfzteile24-semantic-green);
    border-radius: 8px;
    background-color: var(--color-success-green);
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;

    .pdp-vehicle-notification__headline {
      flex: 1;
    }
  }

  &__toggle--static {
    cursor: default;
  }

  &--expanded &__toggle {
    border-color: transparent;
    border-radius: 8px 8px 0 0;
  }

  &__chevron {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    transition: transform var(--time-S, 0.2s) ease-in-out;

    ::v-deep path {
      fill: currentColor;
    }

    &--expanded {
      transform: rotate(180deg);
    }

    @media #{$_mediaLUp} {
      .pdp-vehicle-notification--expanded & {
        display: none;
      }
    }
  }

  &__attributes {
    width: 100%;
    margin: 0 !important;
    padding: 0 var(--space-1--half) var(--space-1--half);
    background-color: var(--color-white);
  }

  &__attributes--list {
    display: flex;
    flex-direction: column;
  }

  &__row {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    column-gap: var(--space-XS);
    padding: 6px 0;
    border-bottom: 1px solid var(--color-cci-blue-light);

    &:last-child {
      border-bottom: none;
    }
  }

  &__label {
    white-space: nowrap;
  }

  &__value {
    font-weight: bold;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  &__more {
    align-self: flex-start;
    margin: 0;
    padding: 6px 0 0;
    border: none;
    background: none;
    font: inherit;
    color: var(--color-nero);
    text-decoration: underline;
    cursor: pointer;
  }

  &__attributes--chips {
    display: flex;
    flex-wrap: nowrap;
    overflow: hidden;
    gap: 4px;
    max-width: 100%;

    @media #{$_mediaMDown} {
      flex-wrap: wrap;
      overflow: visible;
    }
  }

  &__chip {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    background-color: var(--color-silver-fog);
    border-radius: 3px;
    padding: 2px 4px;
    font-size: var(--font-size-M);
    line-height: 20px;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--color-nero);
    cursor: pointer;

    &:first-child {
      flex: 0 0 auto;
    }

    &--overflow {
      flex: 0 0 auto;
    }

    @media #{$_mediaMDown} {
      flex: 0 0 auto;
      min-width: auto;
      max-width: 100%;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: var(--space-0--half);
    min-width: 0;
    flex: 1;
  }

  &__headline {
    margin: 0;
    font-weight: bold;
    font-size: var(--font-size-M);
    line-height: 20px;
    word-break: break-word;
  }

  &__description {
    margin: 0;
    font-size: var(--font-size-S);
    line-height: 16px;
  }

  &__link {
    margin: 0;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    border: none;
    background: none;
    color: var(--color-black);
    font-size: var(--font-size-M);
    line-height: 18px;
    text-align: left;

    @media #{$_mediaMDown} {
      display: block;
      margin-left: 0;
    }
  }
}
</style>
